/**
 * Copyright © 2020-2023 Delicious AI, LLC
 *
 * @author Wade Wilkey <wade.wilkey@deliciousai.com>
 */

import { ApolloLink, FieldFunctionOptions } from '@apollo/client'
import { ReadFieldFunction } from '@apollo/client/cache/core/types/common'

type PaginatedObject = {
  count: number
  hasMore: boolean
  results: any[]
}

const dedupById = (results: any[], readField: ReadFieldFunction): any[] => {
  return results.reduce<any[]>((acc, item) => {
    const match = acc.find(
      exItem => readField('id', exItem) === readField('id', item),
    )
    if (match) {
      return acc
    } else {
      return [...acc, item]
    }
  }, [])
}

export const mergePaginatedResults = (
  existing: PaginatedObject | undefined,
  incoming: PaginatedObject,
  options: FieldFunctionOptions,
) => {
  const { readField } = options
  if (incoming.results.length) {
    let results: any[] = []
    if (existing?.results?.length) {
      const existingIds = new Set(
        existing.results.map((item: any) => readField('id', item)),
      )

      const uniqueExisting = dedupById(existing.results, readField)

      const uniqueIncoming = incoming.results.filter(
        (item: any) => !existingIds.has(readField('id', item)),
      )

      results = [...uniqueExisting, ...uniqueIncoming]
    } else {
      results = [...incoming.results]
    }
    return {
      ...incoming,
      results,
    }
  }
  return existing?.results ? dedupById(existing?.results, readField) : existing
}

export const mergePreferIncoming = (existing: any, incoming: any) => {
  if (incoming.length === 0) {
    return existing
  }
  return incoming
}

export const keyArgsIgnoreOffsetLimit = (args: any) => {
  if (args?.input) {
    const { offset, limit, ...rest } = args.input
    return JSON.stringify(Object.values(rest))
  }
}

export const addOperationNameToUrl = new ApolloLink((operation, forward) => {
  const context = operation.getContext()
  const contextURI = context.uri || '/graphql'
  operation.setContext({
    ...context,
    uri: `${contextURI}?${operation.operationName}`,
  })

  return forward(operation)
})
